<template>
    <div class="modal-container">
        <div class="modal-header">
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <simple-wizard plain :show-header="false" title="Send Sms" sub-title="" @stepChange="stepChange">
                <wizard-tab :before-change="validateDetails" :id="'step1'">
                    <template slot="label">
                        Create SMS Template
                    </template>
                    <div>
                        <form-wrapper :validator="$v.form" class="form-wrapper">
                            <form-group class="custom-merge-field">
                                <drop-down>
                                    <md-button
                                        @click.prevent
                                        slot="title"
                                        class="dropdown-toggle md-info"
                                        data-toggle="dropdown"
                                    >
                                        Insert Merge Field
                                    </md-button>
                                    <ul class="dropdown-menu dropdown-menu-right">
                                        <li v-for="event in mergeFields" :key="event.key">
                                            <span v-if="event.key === 'stopcustomfields'" class="custom-field-header">
                                                {{ event.name }}
                                            </span>
                                            <a v-else @click="getSelectedMergeField(event.key)">{{ event.name }}</a>
                                        </li>
                                    </ul>
                                </drop-down>
                            </form-group>
                            <form-group class="custom-textarea" name="template">
                                <md-textarea v-model="form.template" ref="template" />
                            </form-group>
                        </form-wrapper>
                    </div>
                </wizard-tab>
                <wizard-tab :before-change="validateDetails" :id="'step2'">
                    <template slot="label">
                        Confirmation
                    </template>
                    <div class="confirmation-message">
                        Are you sure you want to send SMSs to {{ orderContact.phone }}?
                        <br />
                        Click the "Finish" button to send.
                    </div>
                </wizard-tab>
            </simple-wizard>
        </div>
    </div>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { required } from 'vuelidate/lib/validators';
import { SimpleWizard, WizardTab } from '@/components';
import { INSERT_ORDER_MERGE_FIELDS_CONSTANTS } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';

export default {
    name: 'SendSmsModal',
    components: {
        SimpleWizard,
        WizardTab
    },
    mixins: [GeneralMixin],
    props: {
        orderId: {
            type: Number,
            required: true
        },
        orderContact: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            localStorageKey: 'order-details.sms',
            form: {
                key: '',
                template: '',
                level: '',
                eventTrigger: '',
                stopType: null,
                id: 0
            },
            insertMergeFieldOptions: [...INSERT_ORDER_MERGE_FIELDS_CONSTANTS],
            stopCustomMergeFields: [],
            mergeFields: [],
            step: 'step1',
            smsTemplates: []
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    validations: {
        form: {
            key: {
                required
            },
            template: {
                required
            }
        },
        step1: {},
        step2: {},
        step3: {}
    },
    methods: {
        getMergeFields() {
            this.mergeFields = this.insertMergeFieldOptions.sort((a, b) => a.name.localeCompare(b.name));
        },
        getSelectedMergeField(value) {
            const textarea = this.$refs.template.$el;

            if (value !== 'default') {
                if (!this.form.template) {
                    this.form.template = '';
                }
                const temp = this.form.template;
                const currentCursorPosition = textarea.selectionStart;
                this.form.template = `${temp.substring(0, textarea.selectionStart)}{{${value}}}${temp.substring(
                    textarea.selectionEnd,
                    temp.length
                )}`;

                this.$nextTick(() => {
                    textarea.focus();
                    // The additional + 4 is because we are adding {{}} to the merge field value upon insert.
                    const cursorfocusPos = currentCursorPosition + value.length + 4;
                    textarea.setSelectionRange(cursorfocusPos, cursorfocusPos);
                });
            }
        },
        stepChange(name) {
            this.step = name;
        },
        async validateDetails() {
            switch (this.step) {
                case 'step1':
                    if (this.form.template === '' || this.form.template === null) {
                        this.$notify({
                            message: 'Template cannot be empty',
                            type: 'danger'
                        });
                        return false;
                    }
                    break;
                case 'step2':
                    this.sendSms();
                    break;
                default:
                    break;
            }

            return true;
        },
        async sendSms() {
            try {
                const api = '/api/orders/bulk/send-sms';
                const payload = {
                    method: 'post',
                    data: {
                        template: this.form.template,
                        bulkSmsData: [
                            {
                                orderId: this.orderId,
                                phoneNumber: this.orderContact.phone
                            }
                        ]
                    }
                };
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Your SMS is now being sent.',
                    type: 'success'
                });
                localStorage.setItem(this.localStorageKey, JSON.stringify(this.form.template));
                this.$modal.hide();
            } catch (error) {
                const message = 'Unable to send SMS.';
                showErrorMessage(this, message, error);
            }
        }
    },
    mounted() {
        this.getMergeFields();
        if (localStorage.getItem(this.localStorageKey) != null) {
            this.form.template = JSON.parse(localStorage.getItem(this.localStorageKey));
        }
    }
};
</script>

<style lang="scss" scoped>
.overflow-container {
    overflow: auto;
    max-height: 50vh;
    margin-bottom: 10px;
    > div {
        margin-bottom: 20px;
    }
}
.modal-container {
    max-width: 800px;
    @media (min-width: 1440px) {
        max-width: 1000px;
    }
    .modal-header {
        padding: 0;
        button {
            z-index: 99;
        }
    }
    .modal-body {
        padding-top: 0;
        h5 {
            font-weight: 400;
            text-transform: uppercase;
            font-size: 15px;
        }

        ::v-deep .md-card {
            margin: 0 !important;
            background-color: #fff;
            box-shadow: none;

            .md-card-content {
                max-height: 60vh;
            }
        }

        ::v-deep .tab-content {
            padding: 20px 15px 0 15px !important;
        }
        .md-icon-button,
        ::v-deep .md-icon-button {
            background-color: #2b93ff !important;
        }

        .custom-merge-field {
            ::v-deep label {
                top: -10px;
            }
            ::v-deep .dropdown .md-button {
                background-color: #00bcd4 !important;
            }
            .dropdown-menu-right {
                max-height: 300px;
                overflow: auto;
                a {
                    cursor: pointer;
                }
            }
            .custom-field-header {
                padding-left: 10px;
                font-weight: bold;
            }
        }
        .event-trigger {
            float: left;
            width: 180px;
            margin-top: 29px;
            margin-right: 10px;
            ::v-deep label {
                top: -1.2rem !important;
            }
        }

        .stop-table-container {
            ::v-deep .md-table {
                overflow-x: initial;
            }
            min-height: 50vh;
            max-height: 50vh;
            max-width: 100%;
            overflow: auto;
            .stop-checkbox {
                margin: 0;
                margin-top: 6px;
            }
            .error-icon {
                display: inline-block;
                i {
                    color: #ffcc00;
                }
            }
            ::v-deep .md-table-cell,
            ::v-deep .md-table-head {
                padding: 0;
                a {
                    color: #1286ff;
                }
            }
            ::v-deep .md-table-cell {
                height: initial;
            }
            ::v-deep .md-table-head {
                padding-bottom: 5px;
                .md-table-head-label {
                    color: #4caf50 !important;
                }
            }
            .assigned-column {
                max-width: 200px;
            }
        }
        .confirmation-message {
            text-align: center;
            margin-top: 10%;
            font-size: 20px;

            .alert {
                font-size: 16px;
            }
        }
    }
}

.md-tooltip {
    z-index: 9999;
}
</style>
